import { useEffect, useState } from "react";
import { get, map, find, values, filter } from "lodash";
import useApp from "hooks/useApp";
import useApi from "hooks/useApi";
import BloodDonation from "assets/icons/blood_donation.svg";
import CancerAwareness from "assets/icons/cancer_awareness.svg";
import CovidVaccination from "assets/icons/covid_vaccination.svg";
import FitnessWellness from "assets/icons/fitness_wellness.svg";
import HealthCampaigns from "assets/icons/health_campaigns.svg";
import HivAwareness from "assets/icons/hiv_awareness.svg";
import MentalHealth from "assets/icons/mental_health.svg";
import SuccessStories from "assets/icons/success_stories.svg";
import Modules from "./Modules";
import Statistics from "./Statistics";
import Events from "./Events";
import Activity from "./Activity";
import { useTranslation } from "react-i18next";

const defaultEvents = {
  data: [
    {
      key: "success_stories",
      title: "Patient Success Stories",
      date: "03-02-2024",
      imageSrc: SuccessStories,
      bgColor: "#d7ecff",
    },
    {
      key: "blood_donation",
      title: "Blood Donation Camp",
      date: "12-01-2024",
      imageSrc: BloodDonation,
      bgColor: "#fecece",
    },
    {
      key: "fitness_wellness",
      title: "Fitness and Wellness Programs",
      date: "12-01-2024",
      imageSrc: FitnessWellness,
      bgColor: "#e8f3d8",
    },
    {
      key: "mental_health",
      title: "Mental Health Awareness",
      date: "27-12-2023",
      imageSrc: MentalHealth,
      bgColor: "#fff6d9",
    },
    {
      key: "cancer_awareness",
      title: "Cancer Awareness Events",
      date: "23-11-2023",
      imageSrc: CancerAwareness,
      bgColor: "#ffd9d9",
    },
    {
      key: "health_campaigns",
      title: "Health Education Campaigns",
      date: "11-09-2023",
      imageSrc: HealthCampaigns,
      bgColor: "#e8f3d8",
    },
    {
      key: "covid_vaccination",
      title: "Covid-19 Vaccination Camp",
      date: "08-09-2023",
      imageSrc: CovidVaccination,
      bgColor: "#ffebd8",
    },
    {
      key: "hiv_awareness",
      title: "HIV Awareness",
      date: "17-08-2023",
      imageSrc: HivAwareness,
      bgColor: "#ffd7ef",
    },
  ],
  name: "Recent Events",
};

const defaultStatistics = (t) => [
  {
    key: "visits",
    title: t("TOTAL_VISITS"),
    count: 0,
    name: "Total Visits",
  },
  {
    key: "patients",
    title: t("TOTAL_PATIENTS"),
    count: 0,
    name: "Total Patients",
  },
  {
    key: "treatment",
    title: t("ONGOING_TREATMENT"),
    count: 0,
    name: "Ongoing Treatment",
  },
  {
    key: "discharged",
    title: t("DISCHARGED"),
    count: 0,
    name: "Discharged",
  },
];

export default function HomePage(props) {
  const app = useApp();
  const api = useApi();
  const { t } = useTranslation();
  const activityData = {
    labels: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    datasets: [
      {
        label: t("WEEK"),
        data: [40, 55, 56, 80, 65, 59, 70],
        fill: false,
        tension: 0,
      },
    ],
    name: "Activity",
  };

  const [statistics, setStatistics] = useState(defaultStatistics(t));
  const [activity, setActivity] = useState(activityData);
  const [events, setEvents] = useState(defaultEvents || []);
  const [fetchInProgress, setFetchInProgress] = useState(false);
  useEffect(() => {
    const fetchableSettings = filter(app.allSettings, (setting) => {
      return find(
        statistics,
        (statistic) =>
          get(setting, "setting_name") === get(statistic, "name", "") ||
          get(setting, "setting_name") === get(activity, "name", "") ||
          get(setting, "setting_name") === get(events, "name", "")
      );
    });
    Promise.all(
      map(fetchableSettings, (setting) => {
        let url = `dashboard/stats/${setting.id}`;
        const selectedOrgCode = get(app, "selectedOrg.code");
        if (selectedOrgCode) {
          url += `/${selectedOrgCode}`;
        }
        return api.get(url);
      })
    )
      .then((resArr) => {
        const recentEvents = find(resArr, (res) => {
          return res.data.result.setting.setting_name === "Recent Events";
        });
        if (recentEvents) {
          setEvents(recentEvents.data.result);
        }
        const updatedStatistics = map(statistics, (statistic) => {
          const statsWithVal = find(resArr, (res) => {
            const result = get(res, "data.result");
            const settingName = get(result, "setting.setting_name");
            return get(statistic, "name") === settingName;
          });
          if (statsWithVal) {
            const data = get(statsWithVal, "data.result.data", []);

            if (data.length > 0) {
              const val = values(data[0])[0];
              return {
                ...statistic,
                count: val,
              };
            }
          }
          return statistic;
        });
        const activityResp = find(
          resArr,
          (res) =>
            get(res, "data.result.setting.setting_name") ===
            get(activity, "name", "")
        );

        if (activityResp) {
          const activityRespData = get(activityResp, "data.result.data", []);
          if (activityRespData.length > 0) {
            const updatedActivity = {
              ...activity,
              labels: map(activityRespData, (data, index) =>
                t(get(data, "day_of_week", activity.labels[index]).trim())
              ),
              datasets: [
                {
                  ...activity.datasets[0],
                  data: map(activityRespData, (data, index) =>
                    get(data, "avg_mrn_count", activity.datasets[0].data[index])
                  ),
                },
              ],
            };
            setActivity(updatedActivity);
          }
        }
        const eventResp = find(
          resArr,
          (res) =>
            get(res, "data.result.setting.setting_name") ===
            get(events, "name", "")
        );
        if (eventResp) {
        }
        setStatistics(updatedStatistics);
        setFetchInProgress(false);
      })
      .catch((e) => {
        setStatistics(defaultStatistics);
        setFetchInProgress(false);
      });
    app.getAllModules();
  }, [app.allSettings, app?.selectedOrg?.code, t]);
  return (
    <main id="HomePage" className="main-page">
      <section className="page-section q-pa-md">
        <div className="container-fluid">
          <div className="grid">
            <div className="col-8">
              <Modules
                onCardClick={(moduleId) => {
                  app.navigate(`/dashboard/${moduleId}?legend=0`);
                }}
              />
            </div>
            <div className="col-4">
              <Statistics data={statistics} fetchInProgress={fetchInProgress} />
            </div>
            <div className="col-8">
              <Events data={events.data} />
            </div>
            <div
              className="col-4"
              style={{
                paddingLeft: 15,
                paddingTop: 20,
              }}
            >
              <Activity data={activity} fetchInProgress={fetchInProgress} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
